<script>
  /**
   * App shell + route table.
   *
   * Structure copied from plugins/augurly/svelte/src/App.svelte, which is the
   * only shape of this library's API proven to work in this repo:
   *
   *   <RouterContext {options}>  ...shell...  <RouterView> ...snippets... </RouterView>
   *
   * Do not "simplify" this. Two variants were tried and both threw
   * "Router context is missing" from route components' useRouter() at init:
   *   - dropping RouterContext and passing {options} to RouterView
   *   - dropping RouterContext and passing a pre-built {router} to RouterView
   * RouterView must find an existing context, not create its own.
   *
   * Routes are STATICALLY imported, also matching augurly. Lazy
   * `() => import(...)` route components are instantiated in the async
   * continuation after the dynamic import resolves, outside the component-init
   * context that getContext() requires — same error, from RouteComponent's
   * onLoaded path.
   *
   * Role gating is NOT done here. Routes carry `meta.requiresRole` and each
   * protected route gets a `beforeEnter` guard attached in routes.js. This
   * component previously wrapped routes in <AuthGuard> from a
   * `{#snippet fallback(route)}`, which never ran — `fallback` is not a prop of
   * this library; its snippets are `routing`, `loading`, `error`, `children`.
   */
  import { RouterView, RouterContext } from '@dvcol/svelte-simple-router/components';
  import AppSidebar from './components/AppSidebar.svelte';
  import ToastContainer from './components/ToastContainer.svelte';
  import { authStore } from './stores/authStore.svelte.js';

  import { routes, BASE } from './routes.js';

  // The route table lives in routes.js so tests can assert on it as plain data
  // (ordering, role metadata, and that nothing has been converted back to a
  // lazy import). See tests/unit/routes.test.js.
  const options = { routes, base: BASE };

  // Portal context (role + scope) is loaded once per session. Without it the
  // sidebar cannot know which nav items to show, so it is fetched before the
  // shell renders rather than per-route.
  let contextLoading = $state(true);

  $effect(() => {
    if (!authStore.isAuthenticated) {
      contextLoading = false;
      return;
    }
    if (authStore.context) {
      contextLoading = false;
      return;
    }
    (async () => {
      try {
        await authStore.ensureContext();
      } catch (e) {
        // A 403 here means an authenticated chassis account with no portal
        // membership. Sign them out rather than showing an empty shell.
        if (e.status === 403) authStore.clearAuth();
      } finally {
        contextLoading = false;
      }
    })();
  });
</script>

<RouterContext {options}>
  <div class="flex min-h-screen">
    {#if authStore.isAuthenticated && authStore.context}
      <AppSidebar base={BASE} />
    {/if}

    <div class="flex min-w-0 flex-1 flex-col">
      <main class="flex-1 overflow-y-auto"
            class:px-4={authStore.isAuthenticated}
            class:py-8={authStore.isAuthenticated}
            class:pt-16={authStore.isAuthenticated}
            class:md:pt-8={authStore.isAuthenticated}
            class:sm:px-6={authStore.isAuthenticated}
            class:lg:px-8={authStore.isAuthenticated}>
        <div class={authStore.isAuthenticated ? 'mx-auto max-w-7xl' : ''}>
          {#if contextLoading}
            <div class="flex items-center justify-center py-24">
              <span class="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent"
                    style="color: var(--p-brand)" role="status" aria-label="Loading"></span>
            </div>
          {:else}
            <RouterView>
              {#snippet loading()}
                <div class="flex items-center justify-center py-24">
                  <span class="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent"
                        style="color: var(--p-brand)"></span>
                </div>
              {/snippet}

              {#snippet error(err)}
                <p class="p-card p-4 text-sm" style="color: var(--p-danger)">
                  This page failed to load: {err?.message ?? 'unknown error'}
                </p>
              {/snippet}

            </RouterView>
          {/if}
        </div>
      </main>
    </div>
  </div>

  <ToastContainer />
</RouterContext>
